/* eslint-disable react/jsx-no-target-blank */
import React from 'react';
import YouTube from 'react-youtube';

import s from '../styles/ClubInfo.module.css';
import tabPanel from '../styles/TabPanel.module.css';
import { CourseProps } from '../props';
import { URL } from '@Pages/url';
import { cleanCourseName } from '@Util/util';
import Heading2 from '@Components/heading_2/view';

type ClubInfoProps = {
    show: boolean;
    course: CourseProps;
    courseName: string;
    courseKanaName: string;
    holiday: string;
    children: React.ReactNode;
    isSp: boolean;
};
export const ClubInfo = (props: ClubInfoProps) => {
    const youtubeOptions = props.isSp ? { width: 350, height: 197 } : { width: 850, height: 478 };

    return (
        <div className={`${s.club_info} ${props.show ? '' : tabPanel.tab_panel_hidden}`}>
            <div className={s.club_info_container}>
                <div className={s.club_info_box}>
                    <Heading2 title={`${cleanCourseName(props.courseName)}のコース情報`} />
                    <div className={s.club_info_columns}>
                        <div className={s.club_info_column}>
                            <p className={s.club_info_column_title}>ホール数</p>
                            <p className={s.club_info_column_value}>{props.course.hole || '-'}</p>
                        </div>
                        <div className={s.club_info_column}>
                            <p className={s.club_info_column_title}>全長</p>
                            <p className={s.club_info_column_value}>{props.course.courseLength || '-'}yd</p>
                        </div>
                        <div className={s.club_info_column}>
                            <p className={s.club_info_column_title}>パー</p>
                            <p className={s.club_info_column_value}>{props.course.par || '-'}</p>
                        </div>
                        <div className={s.club_info_column}>
                            <p className={s.club_info_column_title}>コースレート</p>
                            <p className={s.club_info_column_value}>{props.course.rate || '-'}</p>
                        </div>
                    </div>
                    <div className={s.club_info_columns}>
                        <div className={s.club_info_column}>
                            <p className={s.club_info_column_title}>練習場</p>
                            <p className={s.club_info_column_value}>{props.course.practiceGround || '-'}</p>
                        </div>
                        <div className={s.club_info_column}>
                            <p className={s.club_info_column_title}>コース設計</p>
                            <p className={s.club_info_column_value}>{props.course.courseDesigner || '-'}</p>
                        </div>
                        {props.course.seriesId && (
                            <div className={`${s.club_info_column} ${s.club_info_column_course}`}>
                                <p className={`${s.club_info_column_title} ${s.club_info_title_course}`}>系列コース</p>
                                <p className={s.club_info_column_value}>
                                    <a href={`${URL.COURSES}?series_id=${props.course.seriesId}`}>
                                        <span>{props.course.seriesName}</span>
                                    </a>
                                </p>
                            </div>
                        )}
                    </div>
                    {props.course.alliances.length > 0 && (
                        <div className={`${s.club_info_column} ${s.club_info_column_course} ${s.club_info_alliance}`}>
                            <p className={`${s.club_info_column_title} ${s.club_info_title_alliance}`}>提携コース</p>
                            <p className={`${s.club_info_column_value} ${s.club_info_column_value_alliance}`}>
                                {props.course.alliances.map((data) => (
                                    <a href={`${URL.COURSES}/${data.id}`} key={data.id}>
                                        <span>{data.name}</span>
                                    </a>
                                ))}
                            </p>
                        </div>
                    )}
                    <div className={s.club_info_introduction}>
                        <span className={s.club_info_introduction_tag}>
                            ゴルフ場のポイント!
                            <span className={s.club_info_introduction_tag_point} />
                        </span>
                        <p className={s.club_info_introduction_text}>{props.course.introduction || '-'}</p>
                    </div>

                    {/*
                     * 動画プレイヤーはタブが非表示の間はアンマウントする。
                     * display:none のまま残すと、再生中に別タブへ切り替えたときに
                     * 音声だけが鳴り続け、止める手段が画面上に無くなる
                     */}
                    {props.show && props.course.youtubeVideoId && (
                        <div className={s.club_info_youtube}>
                            {/*
                             * react-youtubeのrender()はwindow/documentに一切依存しない
                             * plainなdiv2つのみを返す（実DOM操作はcomponentDidMount経由の
                             * createPlayer内のみで、そちらは既にtypeof document==='undefined'
                             * ガード済み・renderToString時は呼ばれない）ため、SSR/CSRで
                             * 出力が完全に一致する。windowガードで分岐させるとSSRだけ空divに
                             * なりhydrationミスマッチを作ってしまうので付けない
                             */}
                            <YouTube videoId={props.course.youtubeVideoId} loading={'lazy'} opts={youtubeOptions} />
                        </div>
                    )}
                </div>
                {props.children}
                <div className={s.course_base_info_box}>
                    <Heading2 title={`${cleanCourseName(props.courseName)}の基本情報`} />
                    <dl className={s.club_info_base_info}>
                        <dt>ゴルフ場名</dt>
                        <dd>
                            {cleanCourseName(props.courseName)}（{props.courseKanaName || '-'}）
                        </dd>
                        <dt>所在地</dt>
                        <dd>
                            &#12306;{props.course.zipcode} {props.course.address}
                        </dd>
                        <dt>連絡先</dt>
                        <dd>
                            <div className={s.club_info_base_info_tag_box}>
                                <span className={s.club_info_base_info_tag}>TEL</span>
                                {props.course.tel || '-'}
                            </div>
                            <div className={s.club_info_base_info_tag_box}>
                                <span className={s.club_info_base_info_tag}>FAX</span>
                                {props.course.fax || '-'}
                            </div>
                        </dd>
                        <dt>開場日</dt>
                        <dd>{props.course.openingDate || '-'}</dd>
                        <dt>定休日</dt>
                        <dd>{props.course.holiday || '-'}</dd>
                        <dt>会員数</dt>
                        <dd>
                            <div>正会員：{props.course.numberOfRegularMembers || '-'}名</div>
                            <div>その他会員数：{props.course.numberOfOtherMembers || '-'}名</div>
                        </dd>
                        <dt>公式HP</dt>
                        <dd>
                            <a href={props.course.websiteUrl} target='_blank'>
                                {props.course.websiteUrl || '-'}
                            </a>
                        </dd>
                        {(props.course.facebookUrl ||
                            props.course.xUrl ||
                            props.course.instagramUrl ||
                            props.course.tiktokUrl ||
                            props.course.youtubeUrl) && (
                            <>
                                <dt>SNSアカウント</dt>
                                <dd>
                                    {props.course.facebookUrl && (
                                        <div className={`${s.sns_icon_facebook} ${s.sns_icon}`}>
                                            <a href={props.course.facebookUrl} className={s.sns_text} target='_blank'>
                                                {props.course.facebookUrl || '-'}
                                            </a>
                                        </div>
                                    )}
                                    {props.course.xUrl && (
                                        <div className={`${s.sns_icon_x} ${s.sns_icon}`}>
                                            <a href={props.course.xUrl} className={s.sns_text} target='_blank'>
                                                {props.course.xUrl || '-'}
                                            </a>
                                        </div>
                                    )}
                                    {props.course.instagramUrl && (
                                        <div className={`${s.sns_icon_instagram} ${s.sns_icon}`}>
                                            <a href={props.course.instagramUrl} className={s.sns_text} target='_blank'>
                                                {props.course.instagramUrl || '-'}
                                            </a>
                                        </div>
                                    )}
                                    {props.course.tiktokUrl && (
                                        <div className={`${s.sns_icon_tiktok} ${s.sns_icon}`}>
                                            <a href={props.course.tiktokUrl} className={s.sns_text} target='_blank'>
                                                {props.course.tiktokUrl || '-'}
                                            </a>
                                        </div>
                                    )}
                                    {props.course.youtubeUrl && (
                                        <div className={`${s.sns_icon_youtube} ${s.sns_icon}`}>
                                            <a href={props.course.youtubeUrl} className={s.sns_text} target='_blank'>
                                                {props.course.youtubeUrl || '-'}
                                            </a>
                                        </div>
                                    )}
                                </dd>
                            </>
                        )}
                    </dl>
                </div>
            </div>
        </div>
    );
};

export default ClubInfo;
